import React, { useState, useEffect } from "react";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import {
    MagnifyingGlassIcon,
    PlusIcon,
    BuildingOffice2Icon,
    UsersIcon,
    MinusCircleIcon,
} from "@heroicons/react/24/outline";
import { Customer, Division, Report } from "../../../types/settings";
import {
    getCustomers,
    getDivisions,
    getReports,
    getCustomerReports,
    addCustomerReport,
    removeCustomerReport,
    createCustomer,
    createDivision,
} from "./api/customer";
import { Combobox } from "components/ui/combobox";

type ReportModalMode = "add" | "remove" | null;

const Customers: React.FC = () => {
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
    const [divisions, setDivisions] = useState<Division[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [divisionsLoading, setDivisionsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [searchTerm, setSearchTerm] = useState<string>("");

    const [createCustomerModalOpen, setCreateCustomerModalOpen] = useState<boolean>(false);
    const [createCustomerLoading, setCreateCustomerLoading] = useState<boolean>(false);
    const [createCustomerError, setCreateCustomerError] = useState<string>("");
    const [customerData, setCustomerData] = useState({
        cust_id: "",
        cust_name: "",
    });

    const [createDivisionModalOpen, setCreateDivisionModalOpen] = useState<boolean>(false);
    const [createDivisionLoading, setCreateDivisionLoading] = useState<boolean>(false);
    const [createDivisionError, setCreateDivisionError] = useState<string>("");
    const [divisionId, setDivisionId] = useState<string>("");
    const [divisionName, setDivisionName] = useState<string>("");

    const [reports, setReports] = useState<Report[]>([]);
    const [allReports, setAllReports] = useState<Report[]>([]);
    const [reportsLoading, setReportsLoading] = useState<boolean>(false);

    const [reportModalMode, setReportModalMode] = useState<ReportModalMode>(null);
    const [reportModalLoading, setReportModalLoading] = useState(false);
    const [reportModalError, setReportModalError] = useState("");
    const [selectedReport, setSelectedReport] = useState<Report | null>(null);
    const [selectedReportId, setSelectedReportId] = useState("");

    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                setLoading(true);
                setError("");
                const response = await getCustomers();
                setCustomers(response || []);
            } catch (err: any) {
                console.error("Error fetching customers:", err);
                setError(err?.message || "Failed to fetch customers");
            } finally {
                setLoading(false);
            }
        };

        fetchCustomers();
    }, []);

    useEffect(() => {
        const fetchCustomerData = async () => {
            if (!selectedCustomer) {
                setDivisions([]);
                setReports([]);
                return;
            }

            try {
                setDivisionsLoading(true);
                setReportsLoading(true);

                const [divisionsResponse, reportsResponse] =
                    await Promise.all([
                        getDivisions(selectedCustomer.cust_id),
                        getCustomerReports(selectedCustomer.cust_id),
                    ]);

                setDivisions(divisionsResponse || []);
                setReports(reportsResponse || []);
            } catch (err) {
                console.error("Error fetching customer data:", err);

                setDivisions([]);
                setReports([]);
            } finally {
                setDivisionsLoading(false);
                setReportsLoading(false);
            }
        };

        fetchCustomerData();
    }, [selectedCustomer]);

    const filteredCustomers = customers.filter(
        (c) =>
            c.cust_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.cust_id.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleCustomerSelect = (customer: Customer) => {
        setSelectedCustomer(customer);
    };

    const handleOpenCreateCustomerModal = () => {
        setCreateCustomerModalOpen(true);
        setCreateCustomerError("");
        setCustomerData({ cust_id: "", cust_name: "" });
    };

    const handleCloseCreateCustomerModal = () => {
        setCreateCustomerModalOpen(false);
        setCreateCustomerError("");
        setCustomerData({ cust_id: "", cust_name: "" });
    };

    const handleCreateCustomer = async () => {
        try {
            setCreateCustomerLoading(true);
            setCreateCustomerError("");

            if (!customerData.cust_id.trim())
                return setCreateCustomerError("Customer ID is required");
            if (!customerData.cust_name.trim())
                return setCreateCustomerError("Customer name is required");

            await createCustomer({
                cust_id: customerData.cust_id.trim(),
                cust_name: customerData.cust_name.trim(),
            });

            const response = await getCustomers();
            setCustomers(response || []);
            handleCloseCreateCustomerModal();
        } catch (err: any) {
            console.error(err);
            setCreateCustomerError(err?.message || "Failed to create customer");
        } finally {
            setCreateCustomerLoading(false);
        }
    };

    const handleOpenCreateDivisionModal = () => {
        if (!selectedCustomer) return;
        setCreateDivisionModalOpen(true);
        setCreateDivisionError("");
        setDivisionId("");
        setDivisionName("");
    };

    const handleCloseCreateDivisionModal = () => {
        setCreateDivisionModalOpen(false);
        setCreateDivisionError("");
        setDivisionId("");
        setDivisionName("");
    };

    const handleCreateDivision = async () => {
        if (!selectedCustomer) return;

        try {
            setCreateDivisionLoading(true);
            setCreateDivisionError("");

            if (!divisionId.trim())
                return setCreateDivisionError("Division ID required");
            if (!divisionName.trim())
                return setCreateDivisionError("Division name required");

            await createDivision({
                cust_id: selectedCustomer.cust_id,
                divn_id: Number(divisionId),
                divn_name: divisionName.trim(),
            });

            const response = await getDivisions(selectedCustomer.cust_id);
            setDivisions(response || []);
            handleCloseCreateDivisionModal();
        } catch (err: any) {
            console.error(err);
            setCreateDivisionError(err?.message || "Failed to create division");
        } finally {
            setCreateDivisionLoading(false);
        }
    };

    const handleOpenAddReportModal = async () => {
        if (!selectedCustomer) return;

        try {
            const response = await getReports(selectedCustomer.cust_id);
            setAllReports(response || []);
            setSelectedReport(null);
            setReportModalError("");
            setReportModalMode("add");
        } catch (err: any) {
            console.error(err);
            setReportModalError(err?.message || "Failed to fetch reports");
        }
    };

    const handleCloseAddReportModal = () => {
        setReportModalMode(null);
        setReportModalError("");
        setSelectedReportId("");
    };

    const handleAddReport = async () => {
        if (!selectedCustomer) return;

        try {
            setReportModalLoading(true);
            setReportModalError("");

            if (!selectedReportId) {
                return setReportModalError("Please select a report");
            }

            await addCustomerReport({
                cust_id: selectedCustomer.cust_id,
                report_id: selectedReportId,
            });

            const updatedReports = await getCustomerReports(
                selectedCustomer.cust_id
            );

            setReports(updatedReports || []);

            handleCloseAddReportModal();
        } catch (err: any) {
            console.error(err);
            setReportModalError(
                err?.message || "Failed to add report"
            );
        } finally {
            setReportModalLoading(false);
        }
    };

    const handleOpenRemoveReportModal = (report: Report) => {
        setSelectedReport(report);
        setReportModalError("");
        setReportModalMode("remove");
    };

    const handleCloseRemoveReportModal = () => {
        setReportModalMode(null);
        setReportModalError("");
        setSelectedReport(null);
    };

    const handleRemoveReport = async () => {
        if (!selectedCustomer || !selectedReport) return;

        try {
            setReportModalLoading(true);
            setReportModalError("");

            await removeCustomerReport({
                cust_id: selectedCustomer.cust_id,
                report_id: selectedReport.report_id,
            });

            const updatedReports = await getCustomerReports(
                selectedCustomer.cust_id
            );

            setReports(updatedReports || []);

            handleCloseRemoveReportModal();
        } catch (err: any) {
            console.error(err);

            setReportModalError(err?.message || "Failed to remove report");
        } finally {
            setReportModalLoading(false);
        }
    };

    const reportOptions = allReports.map((report) => ({
        value: report.report_id,
        label: report.report_name,
    }));

    return (
        <div className="flex h-[calc(100vh-200px)] gap-6">
            <div className="w-1/3 flex flex-col h-full min-h-0">
                <Card className="flex flex-col flex-1 min-h-0">
                    <CardHeader className="pb-4">
                        <CardTitle className="text-base font-medium">
                            Customers
                        </CardTitle>
                    </CardHeader>

                    <CardContent className="flex flex-col flex-1 space-y-4 min-h-0">
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                className="pl-10"
                                placeholder="Search customers..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <Button
                            className="w-full"
                            variant="outline"
                            onClick={handleOpenCreateCustomerModal}
                        >
                            <PlusIcon className="w-4 h-4 mr-2" />
                            Create Customer
                        </Button>

                        <div className="flex-1 space-y-2 overflow-y-auto">
                            {loading ? (
                                <div className="flex justify-center py-8">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-500"></div>
                                </div>
                            ) : error ? (
                                <Alert variant="destructive">
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            ) : filteredCustomers.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    No customers found
                                </div>
                            ) : (
                                filteredCustomers.map((customer) => (
                                    <div
                                        key={customer.cust_id}
                                        onClick={() => handleCustomerSelect(customer)}
                                        className={`p-3 rounded-lg border cursor-pointer transition-all ${selectedCustomer?.cust_id === customer.cust_id
                                            ? "border-blue-200 bg-blue-50 text-blue-900"
                                            : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                                            }`}
                                    >
                                        <div className="flex items-center space-x-3">
                                            <BuildingOffice2Icon className="w-4 h-4" />
                                            <div className="min-w-0 flex-1">
                                                <div className="font-medium text-sm truncate">
                                                    {customer.cust_name}
                                                </div>
                                                <div className="text-xs text-gray-500 truncate">
                                                    ID: {customer.cust_id}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* RIGHT PANEL */}
            <div className="flex-1 min-h-0">
                <div className="h-full flex flex-col min-h-0">
                    <div className="flex-1 flex flex-col min-h-0">
                        {!selectedCustomer ? (
                            <div className="flex justify-center items-center h-full text-gray-500">
                                <div className="text-center">
                                    <BuildingOffice2Icon className="w-8 h-8 mx-auto mb-4 text-gray-300" />
                                    <p>Select a customer to view details</p>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-6 h-full min-h-0">

                                {/* DIVISIONS SECTION */}
                                <div className="flex-1 min-h-0 border rounded-lg p-4 flex flex-col">
                                    <div className="flex justify-between items-center mb-4 shrink-0">
                                        <CardTitle className="text-base font-medium">
                                            Divisions - {selectedCustomer.cust_name}
                                        </CardTitle>

                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={handleOpenCreateDivisionModal}
                                        >
                                            <PlusIcon className="w-4 h-4 mr-2" />
                                            Create Division
                                        </Button>
                                    </div>

                                    <div className="flex-1 overflow-y-auto">
                                        {divisionsLoading ? (
                                            <div className="flex justify-center items-center h-full">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500"></div>
                                            </div>
                                        ) : divisions.length === 0 ? (
                                            <div className="text-center py-8 text-gray-500">
                                                No divisions found
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                {divisions.map((d) => (
                                                    <div
                                                        key={d.divn_id}
                                                        className="p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 flex items-center space-x-3"
                                                    >
                                                        <UsersIcon className="w-4 h-4 text-gray-500" />

                                                        <div>
                                                            <div className="font-medium text-sm">
                                                                {d.divn_name}
                                                            </div>

                                                            <div className="text-xs text-gray-500">
                                                                ID: {d.divn_id}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* REPORTS SECTION */}
                                <div className="flex-1 min-h-0 border rounded-lg p-4 flex flex-col">
                                    <div className="flex justify-between items-center mb-4 shrink-0">
                                        <CardTitle className="text-base font-medium">
                                            Reports
                                        </CardTitle>

                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={handleOpenAddReportModal}
                                        >
                                            <PlusIcon className="w-4 h-4 mr-2" />
                                            Add Report
                                        </Button>
                                    </div>

                                    <div className="flex-1 overflow-y-auto">
                                        {reportsLoading ? (
                                            <div className="flex justify-center items-center h-full">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500"></div>
                                            </div>
                                        ) : reports.length === 0 ? (
                                            <div className="text-center py-8 text-gray-500">
                                                No Reports found
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                {reports.map((report) => (
                                                    <div
                                                        key={report.report_id}
                                                        className="p-3.5 rounded-lg border border-gray-200 hover:bg-gray-50 flex items-center justify-between gap-3"
                                                    >
                                                        <div className="font-medium text-sm">
                                                            {report.report_name}
                                                        </div>

                                                        <button
                                                            type="button"
                                                            onClick={() => handleOpenRemoveReportModal(report)}
                                                            className="text-red-500 hover:text-red-600 transition-colors"
                                                        >
                                                            <MinusCircleIcon className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Dialog
                open={createCustomerModalOpen}
                onOpenChange={setCreateCustomerModalOpen}
            >
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Create New Customer</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        {createCustomerError && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {createCustomerError}
                                </AlertDescription>
                            </Alert>
                        )}

                        <div className="space-y-2">
                            <Label>Customer ID</Label>
                            <Input
                                value={customerData.cust_id}
                                onChange={(e) =>
                                    setCustomerData((p) => ({
                                        ...p,
                                        cust_id: e.target.value,
                                    }))
                                }
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Customer Name</Label>
                            <Input
                                value={customerData.cust_name}
                                onChange={(e) =>
                                    setCustomerData((p) => ({
                                        ...p,
                                        cust_name: e.target.value,
                                    }))
                                }
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={handleCloseCreateCustomerModal}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateCustomer}
                            disabled={createCustomerLoading}
                        >
                            {createCustomerLoading ? "Creating..." : "Create Customer"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={createDivisionModalOpen}
                onOpenChange={setCreateDivisionModalOpen}
            >
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            Create Division - {selectedCustomer?.cust_name}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        {createDivisionError && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {createDivisionError}
                                </AlertDescription>
                            </Alert>
                        )}

                        <div className="space-y-2">
                            <Label>Division ID</Label>
                            <Input
                                type="number"
                                value={divisionId}
                                onChange={(e) => setDivisionId(e.target.value)}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Division Name</Label>
                            <Input
                                value={divisionName}
                                onChange={(e) => setDivisionName(e.target.value)}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={handleCloseCreateDivisionModal}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateDivision}
                            disabled={createDivisionLoading}
                        >
                            {createDivisionLoading ? "Creating..." : "Create Division"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={reportModalMode === "add"}
                onOpenChange={(open) => {
                    if (!open) handleCloseAddReportModal();
                }}
            >
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            Add Report
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        {reportModalError && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {reportModalError}
                                </AlertDescription>
                            </Alert>
                        )}

                        <div className="space-y-2">
                            <Label>Select Report</Label>

                            <Combobox
                                options={reportOptions}
                                value={selectedReportId}
                                onValueChange={setSelectedReportId}
                                placeholder="Select Report"
                                searchPlaceholder="Search reports..."
                                emptyText="No report found."
                                buttonClassName="h-10 text-sm font-normal text-gray-600"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={handleCloseAddReportModal}
                        >
                            Cancel
                        </Button>

                        <Button
                            onClick={handleAddReport}
                            disabled={reportModalLoading}
                        >
                            {reportModalLoading ? "Adding..." : "Add Report"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={reportModalMode === "remove"}
                onOpenChange={(open) => {
                    if (!open) handleCloseRemoveReportModal();
                }}
            >
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            Remove Report
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        {reportModalError && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {reportModalError}
                                </AlertDescription>
                            </Alert>
                        )}

                        <p className="text-sm text-gray-600">
                            Are you sure you want to remove{" "}
                            <span className="font-medium">
                                {selectedReport?.report_name}
                            </span>
                            ?
                        </p>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={handleCloseRemoveReportModal}
                        >
                            Cancel
                        </Button>

                        <Button
                            variant="destructive"
                            onClick={handleRemoveReport}
                            disabled={reportModalLoading}
                        >
                            {reportModalLoading ? "Removing..." : "Remove"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Customers;
